/* ============================================================
   TinyTote — Sticky Add-to-bag (PDP only)
   Proxies the native Dawn product form. No own form, no libraries.
   - Variant selection: untouched (native form holds the selected variant)
   - Add to cart: triggers the native [name="add"] button (native AJAX cart)
   - No duplicate submits: re-entrancy guard + mirrors native disabled state
   - Show/hide: IntersectionObserver on the native button (no scroll handlers)
   - Price/availability: scoped MutationObserver on the native price node
   - Visibility limited to < 990px via CSS (desktop uses Dawn sticky info)
   ============================================================ */
(function () {
  if (customElements.get('tt-sticky-atc')) return;

  class TtStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.priceSource = document.getElementById(this.dataset.priceSource);
      this.btn = this.querySelector('[data-tt-sticky-add]');
      this.priceEl = this.querySelector('[data-tt-sticky-price]');
      if (!this.form || !this.btn) return; // native form missing → do nothing

      // Native add-to-cart button we proxy to.
      this.nativeBtn = this.form.querySelector('[name="add"]');
      if (!this.nativeBtn) return;

      this._submitting = false;
      this._onClick = this._onClick.bind(this);
      this.btn.addEventListener('click', this._onClick);

      this._watchVisibility();
      this._watchPriceAndAvailability();
      this._syncAvailability(); // initial
    }

    disconnectedCallback() {
      this.btn && this.btn.removeEventListener('click', this._onClick);
      this._io && this._io.disconnect();
      this._mo && this._mo.disconnect();
    }

    _onClick(e) {
      e.preventDefault();
      if (this._submitting || this.nativeBtn.getAttribute('aria-disabled') === 'true' || this.nativeBtn.disabled) {
        return;
      }
      this._submitting = true;
      this.btn.classList.add('is-loading');
      // Reuse the native button entirely (its handler does the AJAX add + cart drawer).
      this.nativeBtn.click();
      // Release the guard shortly after; the native handler manages its own state.
      window.setTimeout(() => {
        this._submitting = false;
        this.btn.classList.remove('is-loading');
      }, 1200);
    }

    // Show the bar only when the native button is OFF screen.
    _watchVisibility() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: just reveal the bar; CSS still limits it to mobile widths.
        this.hidden = false;
        return;
      }
      this._io = new IntersectionObserver((entries) => {
        const nativeVisible = entries.some((en) => en.isIntersecting);
        this._setVisible(!nativeVisible);
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      this._io.observe(this.nativeBtn);
    }

    _setVisible(show) {
      if (show) {
        this.hidden = false;
        // next frame for the CSS transition
        requestAnimationFrame(() => this.classList.add('is-visible'));
      } else {
        this.classList.remove('is-visible');
        // wait out the transition before fully hiding from AT/tab order
        window.setTimeout(() => {
          if (!this.classList.contains('is-visible')) this.hidden = true;
        }, 250);
      }
    }

    // Mirror the native price text + sold-out state when the variant changes.
    _watchPriceAndAvailability() {
      if (this.priceSource && this.priceEl && 'MutationObserver' in window) {
        this._mo = new MutationObserver(() => this._syncPrice());
        this._mo.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        this._syncPrice();
      }
      // Availability can change without a price-node mutation; also observe the native button.
      if ('MutationObserver' in window) {
        this._moBtn = new MutationObserver(() => this._syncAvailability());
        this._moBtn.observe(this.nativeBtn, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      }
    }

    _syncPrice() {
      // Prefer the regular price node if Dawn split it; else the whole block's text.
      const regular = this.priceSource.querySelector('.price-item--regular, .price-item--sale');
      const text = (regular ? regular.textContent : this.priceSource.textContent).trim();
      if (text) this.priceEl.textContent = text;
    }

    _syncAvailability() {
      const soldOut = this.nativeBtn.disabled || this.nativeBtn.getAttribute('aria-disabled') === 'true';
      this.btn.disabled = soldOut;
      this.btn.textContent = soldOut ? 'Sold out' : 'Add to bag';
    }
  }

  customElements.define('tt-sticky-atc', TtStickyAtc);
})();
